/**
 * CardReaderPad — Fundraising Suite, donor management component G (card-present).
 *
 * In-person "tap/insert a card" at a donor event using a Stripe Terminal reader.
 * Loads the @stripe/terminal-js SDK, mints connection tokens from our backend
 * (routed to the CBO's connected account), discovers + connects a reader, then
 * runs the collect → process → capture flow for a gift tagged to the event.
 * Settlement (ledger + receipt) happens on the succeeded webhook, same as online.
 *
 * A "simulated reader" toggle lets staff exercise the whole flow in Stripe test
 * mode before physical hardware is on site.
 */

import React, { useEffect, useRef, useState } from 'react';
import { loadStripeTerminal } from '@stripe/terminal-js';
import { CheckCircle, CreditCard, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  captureTerminalPayment,
  createTerminalPaymentIntent,
  getTerminalConnectionToken,
} from '@/services/api/donations';

type Phase = 'init' | 'discovering' | 'ready' | 'charging' | 'done' | 'error';

interface Props {
  eventId: number;
  amount: number;
  donorName?: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function CardReaderPad({ eventId, amount, donorName, onClose, onSuccess }: Props) {
  const [phase, setPhase] = useState<Phase>('init');
  const [message, setMessage] = useState('Initializing card reader…');
  const [readerLabel, setReaderLabel] = useState<string | null>(null);
  const [simulated, setSimulated] = useState(false);
  const terminalRef = useRef<any>(null);

  // Discover + connect a reader. Re-run when the simulated toggle changes.
  const connect = async (useSimulated: boolean) => {
    setPhase('discovering');
    setMessage(useSimulated ? 'Connecting to the simulated reader…' : 'Searching for a card reader…');
    try {
      const StripeTerminal = await loadStripeTerminal();
      if (!StripeTerminal) throw new Error('Could not load the Stripe Terminal SDK.');

      // (Re)create the terminal instance.
      const terminal =
        terminalRef.current ||
        StripeTerminal.create({
          onFetchConnectionToken: async () => (await getTerminalConnectionToken()).secret,
          onUnexpectedReaderDisconnect: () => {
            setPhase('error');
            setMessage('The card reader disconnected. Reconnect to continue.');
          },
        });
      terminalRef.current = terminal;

      const discovery = await terminal.discoverReaders({ simulated: useSimulated });
      if (discovery.error) throw new Error(discovery.error.message);
      if (!discovery.discoveredReaders || discovery.discoveredReaders.length === 0) {
        setPhase('error');
        setMessage(
          useSimulated
            ? 'No simulated reader available.'
            : 'No reader found. Make sure a reader is registered to this organization and powered on, or use the simulated reader.',
        );
        return;
      }

      const reader = discovery.discoveredReaders[0];
      const connectResult = await terminal.connectReader(reader);
      if (connectResult.error) throw new Error(connectResult.error.message);

      setReaderLabel(reader.label || reader.id || 'Reader');
      setPhase('ready');
      setMessage('Reader connected. Ready to take a card.');
    } catch (e: any) {
      setPhase('error');
      setMessage(e?.message || 'Could not connect to a card reader.');
    }
  };

  useEffect(() => {
    connect(simulated);
    return () => {
      // Best-effort disconnect on unmount.
      try {
        terminalRef.current?.disconnectReader?.();
      } catch {
        /* noop */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const charge = async () => {
    const terminal = terminalRef.current;
    if (!terminal) return;
    setPhase('charging');
    try {
      // 1. Server creates a card_present PaymentIntent (manual capture).
      setMessage('Preparing the charge…');
      const pi = await createTerminalPaymentIntent({
        amount,
        event_id: eventId,
        donor_name: donorName || undefined,
      });

      // 2. Reader collects the card.
      setMessage('Present the card on the reader…');
      const collect = await terminal.collectPaymentMethod(pi.client_secret);
      if (collect.error) throw new Error(collect.error.message);

      // 3. Process the payment (authorization).
      setMessage('Processing…');
      const process = await terminal.processPayment(collect.paymentIntent);
      if (process.error) throw new Error(process.error.message);

      // 4. Server captures — the succeeded webhook then settles + receipts.
      setMessage('Finalizing…');
      await captureTerminalPayment(pi.payment_intent_id);

      setPhase('done');
      setMessage(`Charged $${amount.toLocaleString('en-US')}. Receipt will be emailed if an address was provided.`);
      onSuccess();
    } catch (e: any) {
      setPhase('error');
      setMessage(e?.message || 'The card payment could not be completed.');
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-sm rounded-lg bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <CreditCard size={18} className="text-blue-600" /> Take a card
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        <div className="mb-4 rounded bg-gray-50 p-3 text-center">
          <div className="text-3xl font-semibold text-gray-900">${amount.toLocaleString('en-US')}</div>
          {donorName ? <div className="mt-0.5 text-sm text-gray-500">{donorName}</div> : null}
          {readerLabel ? <div className="mt-1 text-xs text-gray-400">Reader: {readerLabel}</div> : null}
        </div>

        <div className="mb-4 flex items-center justify-center gap-2 text-center text-sm">
          {phase === 'done' ? (
            <span className="flex items-center gap-2 text-green-700">
              <CheckCircle size={18} /> {message}
            </span>
          ) : phase === 'error' ? (
            <span className="text-red-600">{message}</span>
          ) : (
            <span className="flex items-center gap-2 text-gray-600">
              {(phase === 'discovering' || phase === 'charging' || phase === 'init') && (
                <Loader2 size={16} className="animate-spin" />
              )}
              {message}
            </span>
          )}
        </div>

        {phase === 'done' ? (
          <Button className="w-full" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button className="w-full" disabled={phase !== 'ready'} onClick={charge}>
              {phase === 'charging' ? 'Charging…' : `Charge $${amount.toLocaleString('en-US')}`}
            </Button>
            {phase === 'error' ? (
              <button
                onClick={() => connect(simulated)}
                className="mt-2 w-full text-sm text-blue-600 hover:underline"
              >
                Retry connection
              </button>
            ) : null}
            <label className="mt-3 flex items-center justify-center gap-2 text-xs text-gray-400">
              <input
                type="checkbox"
                checked={simulated}
                onChange={(e) => {
                  const v = e.target.checked;
                  setSimulated(v);
                  terminalRef.current = null; // force a fresh instance for the new mode
                  connect(v);
                }}
              />
              Use simulated reader (Stripe test mode)
            </label>
          </>
        )}
      </div>
    </div>
  );
}
